/**
 * ============================================================================
 * Category Detail Component
 * ============================================================================
 * 
 * Vue détaillée d'une catégorie avec:
 * - Liste des facteurs d'émission disponibles
 * - Recherche et filtres
 * - Sélection pour ajouter une émission
 * 
 * ============================================================================
 */

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { getCategoryDesign } from '@/lib/calculator-design';
import { ArrowLeft, Plus, Search } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface EmissionFactor {
  fe_key: string;
  // Generic fields (fallback)
  category: string;
  subcategory?: string;
  process_name?: string;
  unit?: string;
  factor_value?: number;
  scope?: string;
  source_name?: string;
  // Multilingual - category
  category_en?: string | null;
  category_fr?: string | null;
  category_ar?: string | null;
  // Multilingual - subcategory
  subcategory_en?: string | null;
  subcategory_fr?: string | null;
  subcategory_ar?: string | null;
  // Multilingual - process name
  process_name_en?: string | null;
  process_name_fr?: string | null;
  process_name_ar?: string | null;
  // Multilingual - unit
  unit_en?: string | null;
  unit_fr?: string | null;
  unit_ar?: string | null;
  // Multilingual - scope
  scope_en?: string | null;
  scope_fr?: string | null;
  scope_ar?: string | null;
  // Multilingual - factor unit
  factor_unit?: string | null;
  factor_unit_en?: string | null;
  factor_unit_fr?: string | null;
  factor_unit_ar?: string | null;
  // Descriptions
  explanation_en?: string | null;
  explanation_fr?: string | null;
  explanation_ar?: string | null;
  notes?: string | null;
}

export interface CategoryDetailProps {
  category: string;
  factors: EmissionFactor[];
  completedFactors?: Map<string, { activity_value: number; tco2e: number }>;
  onBack: () => void;
  onSelectFactor: (factor: EmissionFactor) => void;
}

export function CategoryDetail({
  category,
  factors,
  completedFactors,
  onBack,
  onSelectFactor,
}: CategoryDetailProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const { i18n } = useTranslation();
  const design = getCategoryDesign(category);
  const Icon = design.icon;

  // Determine current language
  const lang = i18n.language?.split('-')[0] || 'fr';

  // Helper: pick localized value with fallback chain
  const loc = (en: string | null | undefined, fr: string | null | undefined, ar: string | null | undefined, fallback?: string | null) => {
    if (lang === 'ar') return ar || en || fr || fallback || '';
    if (lang === 'fr') return fr || en || fallback || '';
    return en || fr || fallback || '';
  };

  // Labels localized
  const labels = {
    searchPlaceholder: lang === 'fr' ? 'Rechercher des facteurs d\'émission...' : lang === 'ar' ? 'البحث عن عوامل الانبعاثات...' : 'Search emission factors...',
    factorsAvailable:  lang === 'fr' ? 'facteur(s) d\'émission disponible(s)' : lang === 'ar' ? 'عوامل الانبعاثات المتاحة' : 'emission factor(s) available',
    unitLbl:           lang === 'fr' ? 'Unité :' : lang === 'ar' ? 'الوحدة:' : 'Unit:',
    factorLbl:         lang === 'fr' ? 'Facteur :' : lang === 'ar' ? 'العامل:' : 'Factor:',
    sourceLbl:         lang === 'fr' ? 'Source :' : lang === 'ar' ? 'المصدر:' : 'Source:',
    addBtn:            lang === 'fr' ? 'Ajouter' : lang === 'ar' ? 'إضافة' : 'Add',
    noFactors:         lang === 'fr' ? 'Aucun facteur d\'émission trouvé' : lang === 'ar' ? 'لم يتم العثور على عوامل انبعاثات' : 'No emission factors found',
    adjustSearch:      lang === 'fr' ? 'Essayez d\'ajuster votre recherche' : lang === 'ar' ? 'حاول تعديل بحثك' : 'Try adjusting your search query',
    filled:            lang === 'fr' ? 'Renseigné' : lang === 'ar' ? 'مكتمل' : 'Filled',
  };

  // Localized category name for header
  const localCategory = loc(null, null, null, category); // category header stays as-is (it's the key)

  // Filter factors based on search (search in all language variants)
  const filteredFactors = factors.filter((factor) => {
    const searchLower = searchQuery.toLowerCase();
    return (
      factor.subcategory?.toLowerCase().includes(searchLower) ||
      factor.subcategory_fr?.toLowerCase().includes(searchLower) ||
      factor.subcategory_en?.toLowerCase().includes(searchLower) ||
      factor.process_name?.toLowerCase().includes(searchLower) ||
      factor.process_name_fr?.toLowerCase().includes(searchLower) ||
      factor.process_name_en?.toLowerCase().includes(searchLower) ||
      factor.fe_key.toLowerCase().includes(searchLower)
    );
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={onBack}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex items-center gap-3 flex-1">
          <div className={`p-3 rounded-xl ${design.bgColor}`}>
            <Icon className={`h-6 w-6 ${design.color}`} />
          </div>
          <div>
            <h2 className="text-2xl font-bold">{localCategory}</h2>
            <p className="text-sm text-muted-foreground">
              {factors.length} {labels.factorsAvailable}
            </p>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder={labels.searchPlaceholder}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Factors List */}
      <div className="grid gap-3">
        {filteredFactors.length > 0 ? (
          filteredFactors.map((factor) => {
            const completed = completedFactors?.get(factor.fe_key);
            const isCompleted = !!completed;

            // Localized fields for this factor
            const localProcessName = loc(factor.process_name_en, factor.process_name_fr, factor.process_name_ar, factor.process_name);
            const localSubcategory = loc(factor.subcategory_en, factor.subcategory_fr, factor.subcategory_ar, factor.subcategory);
            const localUnit        = loc(factor.unit_en, factor.unit_fr, factor.unit_ar, factor.unit);
            const localScope       = loc(factor.scope_en, factor.scope_fr, factor.scope_ar, factor.scope);
            const localFactorUnit  = loc(factor.factor_unit_en, factor.factor_unit_fr, factor.factor_unit_ar, factor.factor_unit || (localUnit ? `tCO₂e/${localUnit}` : null));

            return (
              <Card
                key={factor.fe_key}
                className={`group cursor-pointer transition-all hover:shadow-md ${
                  isCompleted
                    ? 'border-green-200 bg-green-50/30 hover:border-green-300'
                    : 'hover:border-primary/50'
                }`}
                onClick={() => onSelectFactor(factor)}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 space-y-2">
                      {/* Title */}
                      <div className="flex items-start gap-2">
                        <h3 className="font-semibold text-sm group-hover:text-primary transition-colors">
                          {localProcessName || localSubcategory || factor.fe_key}
                        </h3>
                      </div>

                      {/* Completed indicator */}
                      {isCompleted && (
                        <div className="flex items-center gap-2 text-xs text-green-700 font-medium">
                          <Badge variant="outline" className="border-green-300 bg-green-100 text-green-700">
                            ✓ {labels.filled}: {completed.activity_value} {localUnit} = {completed.tco2e.toFixed(4)} tCO₂e
                          </Badge>
                        </div>
                      )}

                      {/* Details */}
                      <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                        {localSubcategory && (
                          <Badge variant="outline" className="text-xs">
                            {localSubcategory}
                          </Badge>
                        )}
                        {localScope && (
                          <Badge variant="secondary" className="text-xs">
                            {localScope}
                          </Badge>
                        )}
                        {localUnit && (
                          <span className="flex items-center gap-1">
                            <span className="font-medium">{labels.unitLbl}</span>
                            {localUnit}
                          </span>
                        )}
                        {factor.factor_value !== undefined && factor.factor_value !== null && (
                          <span className="flex items-center gap-1">
                            <span className="font-medium">{labels.factorLbl}</span>
                            {factor.factor_value} {localFactorUnit || `tCO₂e/${localUnit || 'unit'}`}
                          </span>
                        )}
                      </div>

                      {/* Source */}
                      {factor.source_name && (
                        <div className="text-xs text-muted-foreground">
                          {labels.sourceLbl} {factor.source_name}
                        </div>
                      )}
                    </div>

                    {/* Action Button */}
                    <Button
                      size="sm"
                      variant="outline"
                      className="shrink-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        onSelectFactor(factor);
                      }}
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      {labels.addBtn}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })
        ) : (
          <div className="text-center py-12 text-muted-foreground">
            <p>{labels.noFactors}</p>
            {searchQuery && (
              <p className="text-sm mt-2">
                {labels.adjustSearch}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
